import Table from '@shared/components/Table';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { identificatorsService } from '@modules/additionalSettings/nomenclatures/services/identificators';
import { positionsService } from '@modules/organizationalStructure/positions/services/positions';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import DeleteModal from '@shared/components/DeleteModal';
import IdentificatorValuesForm from './IdentificatorValuesForm';

export default function IdentificatorValues() {
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('mnemonic');
    const [selectedActive, setSelectedActive] = useState('');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentParentValue, setCurrentParentValue] = useState('');
    const [breadcrumbPath, setBreadcrumbPath] = useState([]);
    const location = useLocation();


    const getDetermId = () => {
        const urlParams = new URLSearchParams(location.search);
        const determId = urlParams.get('determId') || localStorage.getItem('selectedDetermId');
        return determId;
    };

    const getDetermMnemonic = () => {
        const urlParams = new URLSearchParams(location.search);
        const mnemonic = urlParams.get('mnemonic') || localStorage.getItem('selectedDetermMnemonic');
        return mnemonic;
    };

    const getDetermTypeDet = () => {
        const urlParams = new URLSearchParams(location.search);
        const typeDet = urlParams.get('typeDet') || localStorage.getItem('selectedDetermTypeDet');
        return typeDet === 'true' || typeDet === true;
    };

    const columns = [
        {
            key: 'value', label: t('additionalSettings.identificators.form.code'),
        },
        {
            key: 'note', label: t('additionalSettings.identificators.form.title'),
        },

    ];

    const getData = async (params = {}) => {
        const determId = getDetermId();
        let parentValue = params.parentValue !== undefined ? params.parentValue : selectedRecord?.value;
        
        // Handle the case where parentValue is 0 - treat it as empty/null
        if (parentValue === 0 || parentValue === '0') {
            parentValue = undefined;
        }

        if (!determId) {
            return {
                content: [],
                totalElements: 0,
                totalPages: 0,
                pageNumber: 0,
                pageSize: 10
            };
        }

        try {
            const response = await identificatorsService.getNomenclatures(determId, parentValue);
            let filteredData = response || [];

            if (selectedActive !== '') {
                const isActive = selectedActive === 'true';
                filteredData = filteredData.filter(item => item.active === isActive);
            }

            return {
                content: filteredData,
                totalElements: filteredData.length,
                totalPages: 1,
                pageNumber: 0,
                pageSize: filteredData.length
            };
        } catch (error) {
            console.error('Error fetching nomenclatures:', error);
            return {
                content: [],
                totalElements: 0,
                totalPages: 0,
                pageNumber: 0,
                pageSize: 10
            };
        }
    };

    const handleSearchClick = async (getDataFunc, params = {}) => {
        try {
            const searchParams = params.searchQuery || {};
            let result;

            // Check if search params have actual values (not just empty strings)
            const hasValidSearchParams = Object.values(searchParams).some(value => 
                value !== null && value !== undefined && value !== ''
            );

            if (hasValidSearchParams) {
                const mnemonic = getDetermMnemonic();

                if (!mnemonic) {
                    console.error('No mnemonic found');
                    return;
                }

                result = await positionsService.getNomenclaturesByMnemonic(mnemonic, searchParams);
            } else {
                // Use identificatorsService when no search params are provided
                const determId = getDetermId();
                if (!determId) {
                    console.error('No determId found');
                    return;
                }
                
                result = await identificatorsService.getNomenclatures(determId, currentParentValue);
            }

            let filteredData = result || [];

            if (selectedActive !== '') {
                const isActive = selectedActive === 'true';
                filteredData = filteredData.filter(item => item.active === isActive);
            }

            setData(filteredData);
        } catch (error) {
            console.error('Error searching data:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const result = await getData();
                setData(result.content);
            } catch (error) {
                console.error('Error loading data:', error);
                notifyError(t('common.errorMessage'));
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const handleOpenModal = (type) => {
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setSelectedRecord(null);
    };

    const handleSuccess = async (item) => {
        const recordToUse = item || selectedRecord;
        const newParentValue = recordToUse?.value;
        setCurrentParentValue(newParentValue);

        // Add to breadcrumb path
        if (recordToUse) {
            setBreadcrumbPath(prev => [
                ...prev,
                {
                    value: recordToUse.value,
                    title: `${recordToUse.value} - ${recordToUse.note}`,
                    record: recordToUse
                }
            ]);
        }

        const searchParams = {};
        if (searchQuery && typeof searchQuery === 'object') {
            if (searchQuery.value) searchParams.value = searchQuery.value;
            if (searchQuery.note) searchParams.note = searchQuery.note;
        }

        const result = await getData({
            parentValue: newParentValue,
            searchQuery: searchParams
        });
        setData(result.content);
        setSelectedRecord(null);
    };

    const handleBack = async () => {
        if (currentParentValue && currentParentValue.length > 0) {
            const newParentValue = currentParentValue.slice(0, -1);
            setCurrentParentValue(newParentValue);

            // Remove last item from breadcrumb
            setBreadcrumbPath(prev => prev.slice(0, -1));

            const searchParams = {};
            if (searchQuery && typeof searchQuery === 'object') {
                if (searchQuery.value) searchParams.value = searchQuery.value;
                if (searchQuery.note) searchParams.note = searchQuery.note;
            }

            const result = await getData({
                parentValue: newParentValue,
                searchQuery: searchParams
            });
            setData(result.content);
            setSelectedRecord(null);
        }
    };

    const handleBreadcrumbClick = async (breadcrumbItem, index) => {
        try {
            if (breadcrumbItem.value === 'root' || index === 0) {
                // Navigate to root level
                setCurrentParentValue('');
                setBreadcrumbPath([]);
                
                const result = await getData({
                    parentValue: '',
                });
                setData(result.content);
                setSelectedRecord(null);
            } else {
                // Navigate to specific breadcrumb level
                const actualIndex = index - 1; // Adjust for root item
                const targetItem = breadcrumbPath[actualIndex];

                if (targetItem) {
                    setCurrentParentValue(targetItem.value);
                    setBreadcrumbPath(breadcrumbPath.slice(0, actualIndex + 1));

                    const searchParams = {};
                    if (searchQuery && typeof searchQuery === 'object') {
                        if (searchQuery.value) searchParams.value = searchQuery.value;
                        if (searchQuery.note) searchParams.note = searchQuery.note;
                    }

                    const result = await getData({
                        parentValue: targetItem.value,
                        searchQuery: searchParams
                    });
                    setData(result.content);
                    setSelectedRecord(null);
                }
            }
        } catch (error) {
            console.error('Error navigating to breadcrumb level:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedRecord) {
            notifyError('No record selected for deletion');
            return;
        }

        try {
            await identificatorsService.deleteNomenclature(selectedRecord.id);
            notifySuccess(t('common.deletedSuccessfully'));
            handleSuccess();
            handleCloseModal();
        } catch (error) {
            console.error('Error deleting nomenclature:', error);
            notifyError(error?.response?.data?.message || t('common.errorMessage'));
        }
    };

    const actionButtons = (
        <>
            {selectedRecord?.determ.readOnly === false && (

                <button
                    className="icon-button"
                    title={t('common.add')}
                    onClick={() => handleOpenModal('add')}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            )}
            {selectedRecord?.determ.readOnly === false && (
                <button
                    className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                    disabled={!selectedRecord}
                    title={t('common.edit')}
                    onClick={() => handleOpenModal('edit')}
                >
                    <FontAwesomeIcon icon={faEdit} />
                </button>
            )}
            {selectedRecord?.determ.readOnly === false && (
                <button
                    className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                    disabled={!selectedRecord}
                    title={t('common.delete')}
                    onClick={() => handleOpenModal('delete')}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            )}
        </>
    );

    const handleCheckboxChange = (index) => {
        const selectedItem = data[index];
        setSelectedRecord(selectedItem);
    };

    const searchOptions = [
        {
            key: 'value',
            label: 'value',
        },
        {
            key: 'note',
            label: 'text',
        },
    ];

    return (
        <>
            <Table
                title={t('common.values')}
                columns={columns}
                data={data}
                loading={loading}
                actionButtons={actionButtons}
                handleCheckboxChange={handleCheckboxChange}
                selectedData={selectedRecord?.id}
                showCustomSearch={false}
                searchOptions={searchOptions}
                showPageSizeSelector={false}
                showPagination={false}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchType={searchType}
                setSearchType={setSearchType}
                showActiveSelect={true}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
                handleSearchClick={handleSearchClick}
                getData={getData}
                showNavigationArrows={getDetermTypeDet()}
                onNavigateForward={handleSuccess}
                onNavigateBack={handleBack}
                canNavigateBack={!!currentParentValue}
                showBreadcrumb={getDetermTypeDet() && breadcrumbPath.length > 0}
                breadcrumbPath={[
                    { value: 'root', title: t('common.beginning') || 'Beginning' },
                    ...breadcrumbPath
                ]}
                onBreadcrumbClick={handleBreadcrumbClick}
            />

            {/* Delete Modal */}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    selectedData={selectedRecord?.id}
                    name={selectedRecord?.mnemonic}
                    entityType="record"
                />
            )}
            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <IdentificatorValuesForm
                    onClose={handleCloseModal}
                    nomenclature={modalState.type === 'edit' ? selectedRecord : null}
                    modalType={modalState.type}
                    onSuccess={handleSuccess}
                    determId={getDetermId()}
                    determMnemonic={getDetermMnemonic()}
                />
            )}
        </>
    );
}